import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "document-collection" }

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import AttachmentCollection from 'SharedComponents/Attachments/AttachmentCollection.vue';
import FolderPickerDialog from 'SharedComponents/Documents/FolderPickerDialog.vue';
import FilePreview from 'SharedComponents/ui/File/FilePreview.vue';
import FloatingWindow from 'SharedComponents/ui/File/FloatingWindow.vue';
import SIcon from 'SharedComponents/ui/Icon/SIcon.vue';
import { AttachmentsApi } from 'SharedDir/backend/api';
import { useConfirmationDialog } from 'SharedDir/common/composables/useConfirmationDialog';
import useFileApi from 'SharedDir/common/composables/useFileApi';
import { useNotifications } from 'SharedDir/common/composables/useNotifications';
import { MakeRequestOptions, makeRequest } from 'SharedDir/common/composables/useRequests';
import { FsView, ServiceKey } from 'SharedDir/constants/file-system';
import { previewKnowFiles } from 'SharedDir/constants/files';
import { AttachmentType } from 'SharedDir/declarations/files';
import { TaskCommentDocumentBare } from 'SharedDir/declarations/project-tasks';
import { removeRecordFromList } from 'SharedDir/utils/helpers';
import { resolveService } from 'SharedDir/utils/vue';


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'DocumentCollection' },
  __name: 'DocumentCollection',
  props: {
    view: { default: FsView.tile },
    data: {},
    readonly: { type: Boolean, default: false },
    folderId: {},
    rootFolderId: {},
    folderEntity: { default: null },
    folderEntityId: { default: null }
  },
  emits: ["changed"],
  setup(__props: any, { emit: __emit }) {

// Действия над файлами в контексте одной папки (folderId).
// «Удалить» разделено: unlink (связь) vs rmdoc (blob) — один файл может быть в N папках.


const props = __props;

const emits = __emit;

const { t } = useI18n();
const { withConfirmation } = useConfirmationDialog();
const { failureNotification } = useNotifications();
const { downloadFile } = useFileApi();
const FS = resolveService(ServiceKey, undefined, true);

const ownData = ref<(TaskCommentDocumentBare | AttachmentType)[]>(props.data);
const loading = ref<boolean[]>([]);
const pickerVisible = ref(false);
const pickerTitle = ref('');
const pickerMode = ref<'move' | 'link'>('move');
const activeFileId = ref<string | null>(null);
// Нельзя move/link в ту же папку, откуда открыт picker — иначе no-op или 400 с бэка.
const excludeFolderIds = computed(() => [props.folderId]);
// Cross-entity move запрещён; link (ярлык) — разрешён.
const pickerAllowedEntity = computed(() =>
  pickerMode.value === 'move' ? props.folderEntity : null
);
const pickerAllowedEntityId = computed(() =>
  pickerMode.value === 'move' ? props.folderEntityId : null
);
const previewAttachment = ref<AttachmentType | null>(null);

const previewTitle = computed(() => {
  if (!previewAttachment.value) {
    return '';
  }
  const extension = previewAttachment.value.extension
    ? `.${previewAttachment.value.extension}`
    : '';
  return `${previewAttachment.value.name}${extension}`;
});

const previewWindowSize = computed(() =>
  previewAttachment.value?.extension?.toLowerCase() === 'pdf'
    ? undefined
    : { height: 750, width: 875 }
);

function makeRequestOptions(index: number): Partial<MakeRequestOptions> {
  return {
    startLoading: () => {
      loading.value[index] = true;
    },
    finishLoading: () => {
      loading.value[index] = false;
    },
  };
}

watch(
  () => props.data,
  (newValue) => {
    ownData.value = newValue;
  }
);

function getExtension(attachment: AttachmentType) {
  return (attachment.extension || '').toLowerCase();
}

function isPreviewable(attachment: AttachmentType) {
  return previewKnowFiles.includes(getExtension(attachment));
}

function openPreview(attachment: AttachmentType) {
  previewAttachment.value = attachment;
}

function downloadDocument(attachment: AttachmentType) {
  downloadFile(attachment.id);
}

function openMovePicker(fileId: string) {
  activeFileId.value = fileId;
  pickerMode.value = 'move';
  pickerTitle.value = t('documents.folderPicker.moveFileTitle');
  pickerVisible.value = true;
}

function openLinkPicker(fileId: string) {
  activeFileId.value = fileId;
  pickerMode.value = 'link';
  pickerTitle.value = t('documents.folderPicker.linkFileTitle');
  pickerVisible.value = true;
}

async function onPickerSelect(targetFolderId: number) {
  if (!activeFileId.value) {
    return;
  }
  if (pickerMode.value === 'move') {
    await makeRequest(() =>
      FS.moveDoc(activeFileId.value as string, props.folderId, targetFolderId)
    );
    // Explorer не узнает о move без локального удаления — props.data обновится на @changed.
    removeRecordFromList(ownData.value, activeFileId.value);
  } else {
    await makeRequest(() => FS.linkDoc(activeFileId.value as string, targetFolderId));
  }
  emits('changed');
}

async function unlinkDocument(index: number) {
  const doc = ownData.value[index] as AttachmentType;
  if (!doc) {
    return;
  }
  withConfirmation(
    {
      title: t('documents.removeFromFolder'),
      message: t('documents.confirmRemoveFromFolder'),
    },
    async () => {
      await makeRequest(() => FS.unlinkDoc(doc.id, props.folderId), makeRequestOptions(index));
      removeRecordFromList(ownData.value, doc.id);
      emits('changed');
    }
  );
}

async function deleteDocumentPermanently(index: number) {
  const doc = ownData.value[index] as AttachmentType;
  if (!doc) {
    return;
  }
  const usages = await AttachmentsApi.getUsages(doc.id);
  // kind=folder — только ярлыки в папках; fk/jsonb блокируют DELETE так же, как на бэке.
  const hasBlockingUsages = usages.usages.some((usage) => usage.kind !== 'folder');
  if (hasBlockingUsages) {
    failureNotification(t('documents.deleteBlocked'));
    return;
  }
  withConfirmation(
    {
      title: t('documents.deletePermanently'),
      message: t('documents.confirmDeletePermanently'),
    },
    async () => {
      await makeRequest(() => FS.rmdoc(doc.id), makeRequestOptions(index));
      removeRecordFromList(ownData.value, doc.id);
      emits('changed');
    }
  );
}

return (_ctx: any,_cache: any) => {
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_menu = _resolveComponent("q-menu")!
  const _directive_close_popup = _resolveDirective("close-popup")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(AttachmentCollection, {
      view: _ctx.view,
      list: _ctx.data,
      "hide-preview": "",
      class: _normalizeClass(["comment-attachments attachment-list", [_ctx.view]])
    }, {
      "attachment-control": _withCtx(({ attachment, index }) => [
        _createElementVNode("div", {
          class: _normalizeClass(["attachment-control", { trembling: index < loading.value.length && loading.value[index] }])
        }, [
          _createVNode(SIcon, {
            class: "document-actions-trigger",
            color: "grey",
            name: "app:actions",
            title: _unref(t)('documents.actionsMenu')
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_menu, {
                class: "document-actions-menu",
                anchor: "bottom right",
                self: "top right"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_q_list, { style: {"min-width":"280px"} }, {
                    default: _withCtx(() => [
                      (isPreviewable(attachment))
                        ? _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                            key: 0,
                            clickable: "",
                            onClick: ($event: any) => (openPreview(attachment))
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_item_section, { side: "" }, {
                                default: _withCtx(() => [
                                  _createVNode(SIcon, {
                                    name: "app:eye",
                                    color: "brand"
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_q_item_section, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_q_item_label, null, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_unref(t)('documents.preview')), 1)
                                    ]),
                                    _: 1
                                  }),
                                  _createVNode(_component_q_item_label, { caption: "" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_unref(t)('documents.previewDesc')), 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 2
                          }, 1032, ["onClick"])), [
                            [_directive_close_popup]
                          ])
                        : _createCommentVNode("", true),
                      _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                        clickable: "",
                        onClick: ($event: any) => (downloadDocument(attachment))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_item_section, { side: "" }, {
                            default: _withCtx(() => [
                              _createVNode(SIcon, {
                                name: "app:download",
                                color: "brand"
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_q_item_section, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_item_label, null, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)('documents.download')), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_q_item_label, { caption: "" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)('documents.downloadDesc')), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 2
                      }, 1032, ["onClick"])), [
                        [_directive_close_popup]
                      ]),
                      (!_ctx.readonly)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createVNode(_component_q_separator),
                            _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                              clickable: "",
                              onClick: ($event: any) => (openMovePicker(attachment.id))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_q_item_section, { side: "" }, {
                                  default: _withCtx(() => [
                                    _createVNode(SIcon, {
                                      name: "app:arrow-normal-right",
                                      color: "brand"
                                    })
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_q_item_section, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_q_item_label, null, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)('documents.move')), 1)
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_q_item_label, { caption: "" }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)('documents.moveDesc')), 1)
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 2
                            }, 1032, ["onClick"])), [
                              [_directive_close_popup]
                            ]),
                            _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                              clickable: "",
                              onClick: ($event: any) => (openLinkPicker(attachment.id))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_q_item_section, { side: "" }, {
                                  default: _withCtx(() => [
                                    _createVNode(SIcon, {
                                      name: "app:link",
                                      color: "brand"
                                    })
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_q_item_section, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_q_item_label, null, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)('documents.copyShortcut')), 1)
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_q_item_label, { caption: "" }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)('documents.copyShortcutDesc')), 1)
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 2
                            }, 1032, ["onClick"])), [
                              [_directive_close_popup]
                            ]),
                            _createVNode(_component_q_separator),
                            _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                              clickable: "",
                              onClick: ($event: any) => (unlinkDocument(index))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_q_item_section, { side: "" }, {
                                  default: _withCtx(() => [
                                    _createVNode(SIcon, {
                                      name: "close",
                                      color: "brand"
                                    })
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_q_item_section, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_q_item_label, null, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)('documents.removeFromFolder')), 1)
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_q_item_label, { caption: "" }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)('documents.removeFromFolderDesc')), 1)
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 2
                            }, 1032, ["onClick"])), [
                              [_directive_close_popup]
                            ]),
                            _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                              clickable: "",
                              onClick: ($event: any) => (deleteDocumentPermanently(index))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_q_item_section, { side: "" }, {
                                  default: _withCtx(() => [
                                    _createVNode(SIcon, {
                                      name: "app:delete-2",
                                      color: "brand-red"
                                    })
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_q_item_section, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_q_item_label, { class: "text-negative" }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)('documents.deletePermanently')), 1)
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_q_item_label, { caption: "" }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)('documents.deletePermanentlyDesc')), 1)
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 2
                            }, 1032, ["onClick"])), [
                              [_directive_close_popup]
                            ])
                          ], 64))
                        : _createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1024)
            ]),
            _: 2
          }, 1032, ["title"])
        ], 2)
      ]),
      _: 1
    }, 8, ["view", "list", "class"]),
    _createVNode(FolderPickerDialog, {
      modelValue: pickerVisible.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pickerVisible).value = $event)),
      "root-folder-id": _ctx.rootFolderId,
      title: pickerTitle.value,
      "exclude-folder-ids": excludeFolderIds.value,
      "allowed-entity": pickerAllowedEntity.value,
      "allowed-entity-id": pickerAllowedEntityId.value,
      onSelect: onPickerSelect
    }, null, 8, ["modelValue", "root-folder-id", "title", "exclude-folder-ids", "allowed-entity", "allowed-entity-id"]),
    (previewAttachment.value)
      ? (_openBlock(), _createBlock(FloatingWindow, {
          key: 0,
          title: previewTitle.value,
          size: previewWindowSize.value,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (previewAttachment.value = null))
        }, {
          default: _withCtx(() => [
            _createVNode(FilePreview, { fileData: previewAttachment.value }, null, 8, ["fileData"])
          ]),
          _: 1
        }, 8, ["title", "size"]))
      : _createCommentVNode("", true)
  ]))
}
}

})